/**
 * Audit Log Service
 *
 * Helper functions for creating consistent log entries.
 * Does NOT replace Redux — components still call addLog/addLogs.
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Create a log entry with consistent shape.
 *
 * @param {Object} params
 * @param {string} params.action - Action type from AUDIT_ACTIONS
 * @param {string} params.description - Human-readable description
 * @param {string} [params.boothKey] - BOOTH_KEY (optional for some actions)
 * @param {string} [params.keyId] - KEY_ID (optional, assigned at save if missing)
 * @param {string} [params.userName] - User name (defaults to "Admin")
 * @returns {Object} Log entry ready for addLog()
 */
export function createLogEntry({ action, description, boothKey, keyId, userName }) {
  const entry = {
    action,
    date: new Date().toLocaleString(),
    description,
    user: userName || 'Admin',
  };

  if (boothKey !== undefined) {
    entry.BOOTH_KEY = boothKey;
  }

  if (keyId !== undefined) {
    entry.KEY_ID = keyId;
  }

  return entry;
}

/**
 * Create multiple log entries for batch operations (e.g., multi-booth move).
 *
 * @param {Array<Object>} items - Array of { action, description, boothKey, keyId, userName }
 * @returns {Array<Object>} Array of log entries ready for addLogs()
 */
export function createLogEntries(items) {
  return items.map((item) => createLogEntry(item));
}

/**
 * Build server envelope for immediate-save paths (import_dwg, delete_dwg).
 * These bypass Redux and go directly to server.
 *
 * @param {Object} logEntry - Log entry from createLogEntry()
 * @param {string} userName - User name for USER_NAME field
 * @returns {Object} Server-ready envelope
 */
export function buildServerEnvelope(logEntry, userName) {
  const keyId = logEntry.KEY_ID || uuidv4();
  return {
    BOOTH_KEY: logEntry.BOOTH_KEY || '',
    KEY_ID: keyId,
    ACTIVE: 1,
    TYPE: 2012,
    LOG_TEXT: {
      ...logEntry,
      KEY_ID: keyId,
    },
    USER_NAME: userName || 'Admin',
  };
}
